import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, X,
  AlertOctagon, Users as UsersIcon, ShieldCheck, Inbox,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminAds.css';

// Chip filters mirror the backend `role_filter` enum exactly.
// iter281 — default chip is `real` so the visible Users tab matches
// the dashboard Real Users KPI card. Operator can still inspect
// raw / test / seed populations via the dedicated chips.
const CHIP_DEFS = [
  { id: 'real',        color: 'green',  en: 'Real Users',         ar: 'مستخدمون حقيقيون' },
  { id: 'all',         color: 'gray',   en: 'All (raw DB)',       ar: 'الكل (قاعدة البيانات)' },
  { id: 'admin',       color: 'blue',   en: 'Admins',             ar: 'المشرفون' },
  { id: 'banned',      color: 'red',    en: 'Banned',             ar: 'محظور' },
  { id: 'suspended',   color: 'yellow', en: 'Suspended',          ar: 'موقوف' },
  { id: 'pending_ban', color: 'yellow', en: 'Pending Ban',        ar: 'بانتظار الحظر' },
  { id: 'test',        color: 'gray',   en: 'Test',               ar: 'اختبار' },
  { id: 'seed',        color: 'gray',   en: 'Seed',               ar: 'بذرة' },
];

const PAGE_SIZE = 25;

export default function AdminUsers() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const location = useLocation();
  const isArabic = lang === 'ar';

  const initialFilter = (() => {
    const hash = location.hash || '';
    const qIdx = hash.indexOf('?');
    const params = new URLSearchParams(
      qIdx >= 0 ? hash.slice(qIdx + 1) : (location.search || '').replace(/^\?/, '')
    );
    const f = params.get('filter') || '';
    // iter281 — default is `real` so the visible Users tab matches the
    // Admin Dashboard Real Users KPI card. The dashboard card is the
    // single source of truth for the "Total Users" headline.
    return CHIP_DEFS.find((c) => c.id === f) ? f : 'real';
  })();

  const [roleFilter, setRoleFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/dashboard/users',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('role_filter', roleFilter);
      if (search) params.set('search', search);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const apiBase = getApiBase();
      const path = `/api/admin/dashboard/users?${params.toString()}`;
      const resp = await fetch(`${apiBase}${path}`, {
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        setError({
          endpoint: '/api/admin/dashboard/users',
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
        });
        return;
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError({
        endpoint: '/api/admin/dashboard/users',
        status: 0,
        message: (e && e.message) ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [token, roleFilter, search, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  const onBan = async (user) => {
    if (!user?.user_id) return;
    const reason = window.prompt(isArabic ? 'سبب الحظر:' : 'Reason for ban:', '');
    if (!reason) return;
    try {
      const r = await fetch(`${getApiBase()}/api/admin/users/action`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, action: 'ban', reason }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        return;
      }
      toast.success(isArabic ? 'تم الحظر' : 'User banned');
      load();
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    }
  };

  const onUnban = async (user) => {
    if (!user?.user_id) return;
    try {
      const r = await fetch(`${getApiBase()}/api/admin/users/action`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, action: 'unban' }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        return;
      }
      toast.success(isArabic ? 'تمت الإزالة من الحظر' : 'User unbanned');
      load();
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    }
  };

  // iter295 — Phase 3: Wallet credit/debit. Admin enters a signed
  // integer delta + reason; backend audit-logs every change.
  const onAdjustWallet = async (user) => {
    if (!user?.user_id) return;
    const deltaStr = window.prompt(
      isArabic
        ? `تعديل رصيد ${user.email || user.user_id} (الحالي: ${user.coins ?? 0}). أدخل رقماً موجباً (إضافة) أو سالباً (خصم):`
        : `Adjust wallet for ${user.email || user.user_id} (current: ${user.coins ?? 0}). Enter a positive (credit) or negative (debit) integer:`,
      ''
    );
    if (deltaStr === null) return;
    const delta = parseInt(String(deltaStr).trim(), 10);
    if (!Number.isFinite(delta) || delta === 0) {
      toast.error(isArabic ? 'أدخل رقماً صحيحاً غير صفر' : 'Enter a non-zero integer');
      return;
    }
    const reason = window.prompt(
      isArabic ? 'السبب (مطلوب — يُحفظ في سجل المراجعة):' : 'Reason (required — saved to audit log):',
      ''
    );
    if (!reason || !reason.trim()) {
      toast.error(isArabic ? 'السبب مطلوب' : 'Reason is required');
      return;
    }
    try {
      const r = await fetch(`${getApiBase()}/api/admin/users/wallet-adjust`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, delta, reason: reason.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = j?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message_ar && isArabic ? detail.message_ar : detail?.message_en) || (isArabic ? 'فشل تعديل الرصيد' : 'Wallet adjust failed');
        toast.error(msg);
        return;
      }
      toast.success(
        isArabic
          ? `تم: ${j.previous_balance} → ${j.new_balance}`
          : `Done: ${j.previous_balance} → ${j.new_balance}`
      );
      load();
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    }
  };

  // iter295 — Phase 3: Promote/Demote user role. Uses the existing
  // /api/admin/users/set-role endpoint which already writes an audit
  // log. Available roles match what backend accepts (super_admin is
  // owner-only).
  const onChangeRole = async (user) => {
    if (!user?.user_id) return;
    const currentRole = user.admin_role || (user.is_admin ? 'admin' : 'user');
    const newRole = window.prompt(
      isArabic
        ? `الدور الحالي: ${currentRole}. أدخل الدور الجديد (user, moderator, admin):`
        : `Current role: ${currentRole}. New role (user, moderator, admin):`,
      currentRole === 'user' ? 'moderator' : 'user'
    );
    if (newRole === null) return;
    const trimmed = String(newRole).trim().toLowerCase();
    if (!['user', 'moderator', 'admin'].includes(trimmed)) {
      toast.error(isArabic ? 'الدور غير صالح' : 'Invalid role');
      return;
    }
    if (trimmed === currentRole) {
      toast(isArabic ? 'لم يتغير الدور' : 'Role unchanged');
      return;
    }
    try {
      const r = await fetch(`${getApiBase()}/api/admin/users/set-role`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, role: trimmed }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j.detail || (isArabic ? 'فشل تغيير الدور' : 'Role change failed'));
        return;
      }
      toast.success(isArabic ? `الدور: ${trimmed}` : `Role: ${trimmed}`);
      load();
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-users-title">
            <UsersIcon size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'إدارة المستخدمين' : 'Users Management'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'الأرقام الرئيسية تستثني السجلات التجريبية والبذرة والمحذوفة والنظام. استخدم المرشحات لفحص الحسابات الأخرى.'
              : 'Primary numbers exclude test, seed, deleted, and system records. Use chips to inspect other accounts.'}
          </p>
          {/* iter281 — Diagnostic ribbon. Same source as
              /api/admin/dashboard-stats so operator can verify
              consistency at a glance. */}
          {data?.diagnostic && (
            <div
              className="admin-users-diagnostic"
              data-testid="admin-users-diagnostic"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, fontSize: 12 }}
            >
              <span
                className="admin-pill green"
                data-testid="admin-users-diag-real"
                title={isArabic ? 'مستخدمون حقيقيون (بعد استبعاد المرشحات)' : 'Real users (after filter)'}
              >
                {isArabic ? 'حقيقي' : 'Real'}: <strong>{data.diagnostic.real_total ?? 0}</strong>
              </span>
              <span
                className="admin-pill gray"
                data-testid="admin-users-diag-test"
                title={isArabic ? 'حسابات اختبار / بذور' : 'Test + seed fixtures'}
              >
                {isArabic ? 'اختبار/بذرة' : 'Test/Seed'}: <strong>{(data.diagnostic.test_total ?? 0) + (data.diagnostic.seed_total ?? 0)}</strong>
              </span>
              <span
                className="admin-pill blue"
                data-testid="admin-users-diag-raw"
                title={isArabic ? 'إجمالي قاعدة البيانات (خام)' : 'Raw DB total (unfiltered)'}
              >
                {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{data.diagnostic.raw_total ?? 0}</strong>
              </span>
              {(data._source || '').length > 0 && (
                <span style={{ color: 'var(--admin-text-dim)', alignSelf: 'center' }} data-testid="admin-users-diag-source">
                  · {data._source}
                </span>
              )}
            </div>
          )}
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-users-refresh">
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-ads-filters">
        <div className="admin-ads-search">
          <Search size={14} />
          <input
            type="text"
            value={searchInput}
            placeholder={isArabic ? 'بحث في الاسم، البريد، أو المعرّف…' : 'Search name, email, or user_id…'}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); setSearch(searchInput); } }}
            data-testid="admin-users-search-input"
          />
          {searchInput && (
            <button type="button" className="admin-btn ghost" onClick={() => { setSearchInput(''); setSearch(''); setSkip(0); }} data-testid="admin-users-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="admin-ads-status-filters" role="tablist">
          {CHIP_DEFS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`admin-pill ${roleFilter === c.id ? c.color : 'gray'}`}
              onClick={() => { setRoleFilter(c.id); setSkip(0); }}
              data-testid={`admin-users-filter-${c.id}`}
              data-active={roleFilter === c.id ? 'true' : 'false'}
              aria-pressed={roleFilter === c.id}
              role="tab"
            >
              {isArabic ? c.ar : c.en}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div
            className="admin-card"
            data-testid="admin-users-error"
            style={{
              margin: 12,
              borderInlineStart: '3px solid #ef4444',
              background: 'rgba(239, 68, 68, 0.06)',
            }}
          >
            <h4 style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <AlertOctagon size={14} />
              {isArabic ? 'تعذر تحميل المستخدمين' : 'Failed to load users'}
            </h4>
            <ul style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, paddingInlineStart: 18 }}>
              <li>{isArabic ? 'النقطة' : 'Endpoint'}: <code data-testid="admin-users-error-endpoint">{error.endpoint}</code></li>
              <li>{isArabic ? 'حالة HTTP' : 'HTTP status'}: <strong data-testid="admin-users-error-status">{error.status}</strong></li>
              <li>{isArabic ? 'التفاصيل' : 'Detail'}: <span data-testid="admin-users-error-detail">{error.message}</span></li>
            </ul>
            <button type="button" className="admin-btn" onClick={load} style={{ marginTop: 6 }} data-testid="admin-users-error-retry">
              <RefreshCw size={14} /> {isArabic ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}

        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>{isArabic ? 'المستخدم' : 'User'}</th>
                <th>{isArabic ? 'الحالة' : 'Status'}</th>
                <th>{isArabic ? 'إعلانات / كتب' : 'Ads / Books'}</th>
                <th>{isArabic ? 'مسجل في' : 'Joined'}</th>
                <th style={{ textAlign: 'end' }}>{isArabic ? 'إجراءات' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center' }}>
                  <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-users-empty">
                  <Inbox size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
                  <div>{isArabic ? 'لا يوجد مستخدمون يطابقون المرشحات.' : 'No users match the selected filters.'}</div>
                </td></tr>
              )}
              {!loading && data.items.map((u) => (
                <tr key={u.user_id} data-testid={`admin-users-row-${u.user_id}`}>
                  <td>
                    <div className="admin-ads-ad-cell">
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt="" className="admin-ads-thumb" loading="lazy" />
                        : <div className="admin-ads-thumb placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <UsersIcon size={14} style={{ opacity: 0.5 }} />
                          </div>}
                      <div className="admin-ads-ad-meta">
                        <div className="admin-ads-ad-title">{u.name || '—'}</div>
                        <div className="admin-ads-ad-id">{u.email || u.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {u.is_admin && (
                        <span className="admin-pill blue" data-testid={`admin-users-pill-admin-${u.user_id}`}>
                          <ShieldCheck size={10} style={{ verticalAlign: 'middle', marginInlineEnd: 3 }} />
                          {u.admin_role || (isArabic ? 'مشرف' : 'admin')}
                        </span>
                      )}
                      {u.is_banned && <span className="admin-pill red" data-testid={`admin-users-pill-banned-${u.user_id}`}>{isArabic ? 'محظور' : 'Banned'}</span>}
                      {u.is_suspended && <span className="admin-pill yellow">{isArabic ? 'موقوف' : 'Suspended'}</span>}
                      {u.account_status === 'pending_ban' && <span className="admin-pill yellow">{isArabic ? 'بانتظار الحظر' : 'Pending ban'}</span>}
                      {u._is_seed && <span className="admin-pill gray">seed</span>}
                      {u._is_test && <span className="admin-pill gray">test</span>}
                      {!u.is_admin && !u.is_banned && !u.is_suspended && u.account_status !== 'pending_ban' && !u._is_seed && !u._is_test && (
                        <span className="admin-pill green">{isArabic ? 'نشط' : 'Active'}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>
                      <strong>{u.ad_count ?? 0}</strong>
                      <span style={{ color: 'var(--admin-text-dim)' }}> / </span>
                      <strong>{u.book_count ?? 0}</strong>
                    </div>
                  </td>
                  <td className="admin-ads-date">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="admin-ads-actions" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {u.is_banned ? (
                        <button type="button" className="admin-btn" onClick={() => onUnban(u)} data-testid={`admin-users-unban-${u.user_id}`}>
                          {isArabic ? 'فك الحظر' : 'Unban'}
                        </button>
                      ) : (
                        <button type="button" className="admin-btn danger" onClick={() => onBan(u)} disabled={u.is_admin} title={u.is_admin ? (isArabic ? 'لا يمكن حظر مشرف' : 'Cannot ban admin') : ''} data-testid={`admin-users-ban-${u.user_id}`}>
                          {isArabic ? 'حظر' : 'Ban'}
                        </button>
                      )}
                      {/* iter295 — Phase 3 deep actions: wallet credit/debit
                          and role change (promote/demote). Both write to
                          admin_audit_logs via their respective endpoints. */}
                      <button
                        type="button"
                        className="admin-btn"
                        onClick={() => onAdjustWallet(u)}
                        title={isArabic ? 'تعديل رصيد المحفظة' : 'Credit / debit wallet'}
                        data-testid={`admin-users-wallet-${u.user_id}`}
                      >
                        {isArabic ? 'محفظة' : 'Wallet'}
                      </button>
                      <button
                        type="button"
                        className="admin-btn"
                        onClick={() => onChangeRole(u)}
                        title={isArabic ? 'تغيير الدور' : 'Change role'}
                        data-testid={`admin-users-role-${u.user_id}`}
                        disabled={u.admin_role === 'super_admin'}
                      >
                        {isArabic ? 'الدور' : 'Role'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-users-total">
            {isArabic
              ? `إجمالي (${(CHIP_DEFS.find(c=>c.id===roleFilter)?.ar) || roleFilter}): ${data.total}`
              : `Total (${(CHIP_DEFS.find(c=>c.id===roleFilter)?.en) || roleFilter}): ${data.total}`}
          </span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="admin-users-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label" data-testid="admin-users-page-label">
              {currentPage} / {pages}
            </span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="admin-users-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
